import React, { useMemo, useState } from "react";
import AdminPanel from "../shared/AdminPanel";
import panelStyles from "../styles/Panel.module.css";
import styles from "../styles/TriggersSection.module.css";

function joinClasses(...values) {
  return values.filter(Boolean).join(" ");
}

function toNumberValue(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function stageLabel(meta, stage) {
  return meta.stages?.find((item) => item.value === stage)?.label || stage;
}

function keyboardLabel(meta, value) {
  return meta.keyboards?.find((item) => item.value === value)?.label || value || "нет";
}

function renderTriggerCard(trigger, meta, isActive, onSelect) {
  return (
    <button
      key={trigger.id}
      type="button"
      className={joinClasses(styles.triggerCard, isActive && styles.triggerCardActive)}
      onClick={() => onSelect(trigger.id)}
    >
      <div className={styles.triggerTitle}>{trigger.title || "Без названия"}</div>
      <div className={styles.triggerMeta}>
        <span className={trigger.enabled ? styles.chip : styles.chipWarn}>
          {trigger.enabled ? "Включен" : "Выключен"}
        </span>
        <span className={styles.chipMute}>{stageLabel(meta, trigger.stage)}</span>
        <span className={styles.chipMute}>
          {trigger.frequency?.mode === "repeat" ? "Повтор" : "Один раз"}
        </span>
      </div>
      {trigger.description ? <div className={styles.description}>{trigger.description}</div> : null}
    </button>
  );
}

function StageTimingFields({ trigger, onNestedChange }) {
  const stage = trigger.stage;
  if (stage === "user_inactive" || stage === "new_user_inactive" || stage === "after_paywall_view") {
    return (
      <>
        <label className={styles.label}>
          Задержка, сек
          <input
            type="number"
            min="0"
            className={panelStyles.input}
            value={trigger.timing?.delaySec || 0}
            onChange={(event) => onNestedChange("timing", "delaySec", toNumberValue(event.target.value))}
          />
        </label>
        {stage === "after_paywall_view" ? (
          <label className={styles.label}>
            TTL оффера, сек
            <input
              type="number"
              min="0"
              className={panelStyles.input}
              value={trigger.timing?.ttlSec || 0}
              onChange={(event) => onNestedChange("timing", "ttlSec", toNumberValue(event.target.value))}
            />
          </label>
        ) : (
          <label className={styles.label}>
            Максимальная задержка, сек
            <input
              type="number"
              min="0"
              className={panelStyles.input}
              value={trigger.timing?.maxDelaySec || 0}
              onChange={(event) => onNestedChange("timing", "maxDelaySec", toNumberValue(event.target.value))}
            />
          </label>
        )}
      </>
    );
  }

  if (stage === "premium_expires") {
    return (
      <label className={styles.label}>
        Дней до окончания
        <input
          type="number"
          min="0"
          className={panelStyles.input}
          value={trigger.timing?.daysBeforeExpire || 0}
          onChange={(event) => onNestedChange("timing", "daysBeforeExpire", toNumberValue(event.target.value))}
        />
      </label>
    );
  }

  if (stage === "session_requests") {
    return (
      <>
        <label className={styles.label}>
          Запросов в сессии
          <input
            type="number"
            min="1"
            className={panelStyles.input}
            value={trigger.timing?.requestCount || 0}
            onChange={(event) => onNestedChange("timing", "requestCount", toNumberValue(event.target.value))}
          />
        </label>
        <label className={styles.label}>
          Окно сессии, сек
          <input
            type="number"
            min="0"
            className={panelStyles.input}
            value={trigger.timing?.withinSec || 0}
            onChange={(event) => onNestedChange("timing", "withinSec", toNumberValue(event.target.value))}
          />
        </label>
      </>
    );
  }

  return null;
}

function ConditionFields({ trigger, onNestedChange }) {
  return (
    <>
      <label className={styles.toggleRow}>
        <input
          type="checkbox"
          checked={Boolean(trigger.conditions?.freeOnly)}
          onChange={(event) => onNestedChange("conditions", "freeOnly", event.target.checked)}
        />
        Только бесплатные пользователи
      </label>

      <label className={styles.label}>
        Порог токенов
        <input
          type="number"
          min="0"
          className={panelStyles.input}
          value={trigger.conditions?.tokensThreshold || 0}
          onChange={(event) => onNestedChange("conditions", "tokensThreshold", toNumberValue(event.target.value))}
        />
      </label>

      <label className={styles.label}>
        Минимальный баланс после отправки
        <input
          type="number"
          min="0"
          className={panelStyles.input}
          value={trigger.conditions?.minBalanceAfterSend || 0}
          onChange={(event) => onNestedChange("conditions", "minBalanceAfterSend", toNumberValue(event.target.value))}
        />
      </label>
    </>
  );
}

const EDITOR_TABS = [
  { id: "general", label: "Основное" },
  { id: "delivery", label: "Доставка" },
  { id: "conditions", label: "Условия" },
  { id: "rewards", label: "Награды" },
  { id: "advanced", label: "Дополнительно" }
];

function TriggerEditorTabs({ value, onChange }) {
  return (
    <div className={styles.editorTabs}>
      {EDITOR_TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          className={value === tab.id ? styles.editorTabActive : styles.editorTab}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function TriggerEditor({
  meta,
  result,
  selectedTrigger,
  currentStageDescription,
  editorTab,
  setEditorTab,
  onNestedChange,
  onUpdateTrigger,
  onDuplicate,
  onRemove
}) {
  return (
    <div className={styles.editorCard}>
      <div className={styles.editorHeader}>
        <div>
          <strong>{selectedTrigger.title || "Без названия"}</strong>
          {currentStageDescription ? <div className={styles.helper}>{currentStageDescription}</div> : null}
        </div>
        <div className={styles.editorActions}>
          <button type="button" className={panelStyles.buttonSecondary} onClick={onDuplicate}>
            Дублировать
          </button>
          <button type="button" className={panelStyles.buttonSecondary} onClick={onRemove}>
            Удалить
          </button>
        </div>
      </div>

      <TriggerEditorTabs value={editorTab} onChange={setEditorTab} />

      {editorTab === "general" ? (
        <div className={styles.fieldGrid}>
          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={Boolean(selectedTrigger.enabled)}
              onChange={(event) => onUpdateTrigger({ enabled: event.target.checked })}
            />
            Включить триггер
          </label>

          <label className={styles.label}>
            Название
            <input
              type="text"
              className={panelStyles.input}
              value={selectedTrigger.title || ""}
              onChange={(event) => onUpdateTrigger({ title: event.target.value })}
            />
          </label>

          <label className={styles.label}>
            Этап
            <select
              className={panelStyles.select}
              value={selectedTrigger.stage}
              onChange={(event) => onUpdateTrigger({ stage: event.target.value })}
            >
              {meta.stages?.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <label className={joinClasses(styles.label, styles.fieldFull)}>
            Описание для админа
            <input
              type="text"
              className={panelStyles.input}
              value={selectedTrigger.description || ""}
              onChange={(event) => onUpdateTrigger({ description: event.target.value })}
            />
          </label>
        </div>
      ) : null}

      {editorTab === "delivery" ? (
        <div className={styles.fieldGrid}>
          <label className={joinClasses(styles.label, styles.fieldFull)}>
            Текст сообщения
            <textarea
              rows={6}
              className={panelStyles.textarea}
              value={selectedTrigger.messageText || ""}
              onChange={(event) => onUpdateTrigger({ messageText: event.target.value })}
            />
          </label>

          <label className={styles.label}>
            Клавиатура
            <select
              className={panelStyles.select}
              value={selectedTrigger.keyboardType || "none"}
              onChange={(event) => onUpdateTrigger({ keyboardType: event.target.value })}
            >
              {meta.keyboards?.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.label}>
            Режим частоты
            <select
              className={panelStyles.select}
              value={selectedTrigger.frequency?.mode || "once"}
              onChange={(event) => onNestedChange("frequency", "mode", event.target.value)}
            >
              {meta.frequencyModes?.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.label}>
            Макс. отправок на пользователя
            <input
              type="number"
              min="0"
              className={panelStyles.input}
              value={selectedTrigger.frequency?.maxPerUser || 0}
              onChange={(event) => onNestedChange("frequency", "maxPerUser", toNumberValue(event.target.value))}
            />
          </label>

          <label className={styles.label}>
            Кулдаун, сек
            <input
              type="number"
              min="0"
              className={panelStyles.input}
              value={selectedTrigger.frequency?.cooldownSec || 0}
              onChange={(event) => onNestedChange("frequency", "cooldownSec", toNumberValue(event.target.value))}
            />
          </label>

          <StageTimingFields trigger={selectedTrigger} onNestedChange={onNestedChange} />
        </div>
      ) : null}

      {editorTab === "conditions" ? (
        <div className={styles.fieldGrid}>
          <ConditionFields trigger={selectedTrigger} onNestedChange={onNestedChange} />
        </div>
      ) : null}

      {editorTab === "rewards" ? (
        <div className={styles.fieldGrid}>
          <label className={styles.label}>
            Выдать токены
            <input
              type="number"
              min="0"
              className={panelStyles.input}
              value={selectedTrigger.conditions?.grantTokens || 0}
              onChange={(event) => onNestedChange("conditions", "grantTokens", toNumberValue(event.target.value))}
            />
          </label>

          <label className={styles.label}>
            Выдать платные прокруты
            <input
              type="number"
              min="0"
              className={panelStyles.input}
              value={selectedTrigger.conditions?.grantPaidSpins || 0}
              onChange={(event) => onNestedChange("conditions", "grantPaidSpins", toNumberValue(event.target.value))}
            />
          </label>

          <label className={styles.label}>
            Бонусные кредиты фотосессии
            <input
              type="number"
              min="0"
              className={panelStyles.input}
              value={selectedTrigger.conditions?.bonusCredits || 0}
              onChange={(event) => onNestedChange("conditions", "bonusCredits", toNumberValue(event.target.value))}
            />
          </label>
        </div>
      ) : null}

      {editorTab === "advanced" ? (
        <div className={styles.fieldGrid}>
          <label className={styles.label}>
            Ключ локали
            <input
              type="text"
              className={panelStyles.input}
              placeholder="onMessage.aha_offer"
              value={selectedTrigger.messageKey || ""}
              onChange={(event) => onUpdateTrigger({ messageKey: event.target.value })}
            />
          </label>

          <label className={styles.label}>
            Вложение
            <input
              type="text"
              className={panelStyles.input}
              placeholder="photo-123_456"
              value={selectedTrigger.attachment || ""}
              onChange={(event) => onUpdateTrigger({ attachment: event.target.value })}
            />
          </label>

          <label className={joinClasses(styles.label, styles.fieldFull)}>
            Сводка по клавиатуре
            <div className={styles.inlineValue}>{keyboardLabel(meta, selectedTrigger.keyboardType)}</div>
          </label>

          <div className={joinClasses(styles.helper, styles.fieldFull)}>
            Если заполнены и текст сообщения, и ключ локали, приоритет будет у прямого текста. Можно использовать переменные
            {" "}<code>{'{{first_name}}'}</code>, <code>{'{{balance}}'}</code> и <code>{'{{bonusCredits}}'}</code>.
          </div>
        </div>
      ) : null}

      {result ? (
        <div className={result.success ? panelStyles.note : panelStyles.error}>{result.message}</div>
      ) : null}
    </div>
  );
}

function TriggersSectionBody({
  state,
  actions,
  selectedTriggerId,
  selectedTrigger,
  missingRouteTrigger,
  onSelectTrigger,
  onCreateTrigger,
  onDuplicateTrigger,
  onRemoveTrigger
}) {
  const { config, meta, loading, result } = state;
  const [newStage, setNewStage] = useState("user_inactive");
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editorTab, setEditorTab] = useState("general");
  const triggerList = useMemo(
    () => (Array.isArray(config?.triggers) ? config.triggers : []),
    [config?.triggers]
  );
  const enabledCount = useMemo(() => triggerList.filter((item) => item.enabled).length, [triggerList]);
  const stageCount = useMemo(() => new Set(triggerList.map((item) => item.stage)).size, [triggerList]);

  const filteredTriggers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return triggerList.filter((trigger) => {
      if (stageFilter !== "all" && trigger.stage !== stageFilter) return false;
      if (statusFilter === "enabled" && !trigger.enabled) return false;
      if (statusFilter === "disabled" && trigger.enabled) return false;
      if (!needle) return true;
      return [trigger.title, trigger.description, trigger.messageKey, trigger.id]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [search, stageFilter, statusFilter, triggerList]);

  const currentStageDescription = useMemo(
    () => meta.stages?.find((item) => item.value === selectedTrigger?.stage)?.description || "",
    [meta.stages, selectedTrigger?.stage]
  );

  const onNestedChange = (group, key, value) => {
    if (!selectedTrigger) return;
    actions.updateTrigger(selectedTrigger.id, {
      [group]: {
        ...(selectedTrigger[group] || {}),
        [key]: value
      }
    });
  };

  const onUpdateTrigger = (patch) => {
    if (!selectedTrigger) return;
    actions.updateTrigger(selectedTrigger.id, patch);
  };

  return (
    <div className={styles.layout}>
      <div className={styles.sidebar}>
        <div className={styles.toolbar}>
          <div className={panelStyles.chips}>
            <span className={panelStyles.chip}>Всего: {triggerList.length}</span>
            <span className={panelStyles.chip}>Включено: {enabledCount}</span>
            <span className={panelStyles.chip}>Этапов: {stageCount}</span>
          </div>

          <input
            type="text"
            className={panelStyles.input}
            placeholder="Поиск по триггерам"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          <div className={styles.filterRow}>
            <select className={panelStyles.select} value={stageFilter} onChange={(event) => setStageFilter(event.target.value)}>
              <option value="all">Все этапы</option>
              {meta.stages?.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>

            <select className={panelStyles.select} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">Все статусы</option>
              <option value="enabled">Включенные</option>
              <option value="disabled">Выключенные</option>
            </select>
          </div>

          <label className={styles.label}>
            Этап нового триггера
            <select
              className={panelStyles.select}
              value={newStage}
              onChange={(event) => setNewStage(event.target.value)}
            >
              {meta.stages?.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <button type="button" className={panelStyles.button} onClick={() => onCreateTrigger(newStage)}>
            Создать триггер
          </button>

          <div className={panelStyles.note}>
            У каждого выбранного триггера теперь есть свой URL, поэтому на конкретную автоматизацию можно дать прямую ссылку.
          </div>
        </div>

        <div className={styles.triggerList}>
          {filteredTriggers.length > 0 ? (
            filteredTriggers.map((item) => renderTriggerCard(item, meta, item.id === selectedTriggerId, onSelectTrigger))
          ) : (
            <div className={styles.empty}>По текущим фильтрам триггеры не найдены.</div>
          )}
        </div>
      </div>

      <div className={styles.editor}>
        {loading ? <div className={styles.empty}>Загружаем триггеры...</div> : null}

        {!loading && missingRouteTrigger ? (
          <div className={styles.empty}>
            Этот URL триггера не совпадает с текущим конфигом. Выберите другой триггер из списка или создайте новый.
          </div>
        ) : null}

        {!loading && !missingRouteTrigger && selectedTrigger ? (
          <TriggerEditor
            meta={meta}
            result={result}
            selectedTrigger={selectedTrigger}
            currentStageDescription={currentStageDescription}
            editorTab={editorTab}
            setEditorTab={setEditorTab}
            onNestedChange={onNestedChange}
            onUpdateTrigger={onUpdateTrigger}
            onDuplicate={() => onDuplicateTrigger(selectedTrigger.id)}
            onRemove={() => onRemoveTrigger(selectedTrigger.id)}
          />
        ) : null}

        {!loading && !missingRouteTrigger && !selectedTrigger && triggerList.length === 0 ? (
          <div className={styles.empty}>Триггеры еще не настроены. Создайте первую автоматизацию в левом столбце.</div>
        ) : null}
      </div>
    </div>
  );
}

export function TriggersSection({
  mode = "panel",
  isOpen = true,
  onToggle,
  state,
  actions,
  selectedTriggerId,
  selectedTrigger,
  missingRouteTrigger = false,
  onSelectTrigger = actions.setSelectedTriggerId,
  onCreateTrigger = actions.addTrigger,
  onDuplicateTrigger = actions.duplicateTrigger,
  onRemoveTrigger = actions.removeTrigger
}) {
  if (mode === "page") {
    return (
      <TriggersSectionBody
        state={state}
        actions={actions}
        selectedTriggerId={selectedTriggerId}
        selectedTrigger={selectedTrigger}
        missingRouteTrigger={missingRouteTrigger}
        onSelectTrigger={onSelectTrigger}
        onCreateTrigger={onCreateTrigger}
        onDuplicateTrigger={onDuplicateTrigger}
        onRemoveTrigger={onRemoveTrigger}
      />
    );
  }

  return (
    <AdminPanel id="triggers" title="Триггеры" isOpen={isOpen} onToggle={onToggle}>
      <TriggersSectionBody
        state={state}
        actions={actions}
        selectedTriggerId={selectedTriggerId || state.selectedTriggerId}
        selectedTrigger={selectedTrigger || state.selectedTrigger}
        missingRouteTrigger={missingRouteTrigger}
        onSelectTrigger={onSelectTrigger}
        onCreateTrigger={onCreateTrigger}
        onDuplicateTrigger={onDuplicateTrigger}
        onRemoveTrigger={onRemoveTrigger}
      />
    </AdminPanel>
  );
}

export default TriggersSection;
